import { INTEGRATION_PAGE_REPORT } from "../actions/constants";
import { TEST_TAB_KEYS } from "../actions/types";

const { REACT_APP_BASE_URL, REACT_APP_BASENAME } = process.env;

// Get Frontend URL
export const getFrontendURL = (path = "") => `${window.location.origin + REACT_APP_BASENAME}/${path}`;

export const getUrl = (path) => REACT_APP_BASE_URL + path;

export const goToLink = (link) => window.location.assign(link?.startsWith("http") ? link : `https://${link}`);

export const getTestShareLink = (testId, lang) => getUrl(`/test/${testId}${lang ? `?lang=${lang}` : ""}`);

export const getBrowserTestShareLink = (testId, lang) => getFrontendURL(`simply-feedback/${testId}${lang ? `?lang=${lang}` : ""}`);

export const getTemplatesLibraryLink = getUrl("/v2/templates");

export const getTemplateLink = (templateId) => getUrl(`/v2/template/${templateId}`);

export const getLoadTemplateLink = (templateId) => getUrl(`/v2/test/new?template_id=${templateId}`);

/**
 * Marks a study-creation screen with the page the user started from.
 *
 * Leaving a creation screen means going back where you were, and the screen cannot work that out
 * for itself. `project_id` cannot say it: the individual dashboards pass that too, when a study
 * is created inside one of their folders, and those must go on returning to the dashboard. The
 * All Studies page has no id to pass at all — the study it creates is filed in the workspace
 * default — so without a marker of its own it was indistinguishable from the dashboards and
 * Cancel sent the user to one of them.
 *
 * So each page that opens the creation flow names itself, and Cancel returns there.
 */
export const FROM_PARAM = "from";
export const FROM_PROJECT_VALUE = "project";
export const FROM_STUDIES_VALUE = "studies";

/** True when the current screen was opened from a project's own page. */
export const isFromProject = (searchParams) => searchParams?.get(FROM_PARAM) === FROM_PROJECT_VALUE;

/** True when it was opened from the workspace-wide All Studies page. */
export const isFromStudies = (searchParams) => searchParams?.get(FROM_PARAM) === FROM_STUDIES_VALUE;

export const getReportLink = (draftId, tab = "", isSharedReport = false) => {
  return getFrontendURL(!isSharedReport ? `test/${draftId}/report?tab=${tab}` : `share/report/${draftId}?tab=${tab}`);
};

export const getSharedReportLink = (testKey, tab = "") => getFrontendURL(`share/report/${testKey}?tab=${tab}`);

export const getTestPreviewLink = (testId) => getUrl(`/v2/preview/${testId}`);

export const getTestEditLink = (draftId) => getUrl(`/v2/test/${draftId}`);

export const getSlackIntegrationLink = (testId) => getUrl(`/slack/integration/authorize?testId=${testId}`);

export const getGoogleIntegrationLink = (testId) => getUrl(`/slack/integration/googleSheet?testId=${testId}`);

export const getMiroIntegrationLink = (testId, pgType = INTEGRATION_PAGE_REPORT) =>
  getUrl(`/slack/integration/miroOAuth?testId=${testId}&pgType=${pgType}`);

export const getMiroIntegrationLinkForWorkspace = (wrkId) => getUrl(`/slack/integration/miroOAuth?workspaceId=${wrkId}`);

export const getIATestEditLink = (testId) => getUrl(`/v2/tests/${testId}`);

export const getModeratedReportLink = (testId) => getUrl(`/v2/moderated/${testId}/report`);

export const getDownloadProjectReportLink = (testId) => getUrl(`/test/${testId}/moderated/report/downloadProjectReport?tzMin=330`);
export const getVideoReportExportLink = (testId = "", userId = "", type = "csv") => {
  return getUrl(`/test/${testId}/task/report/getTestMetadata?id=${testId}&pg=video&user=${userId}&type=${type}`);
};

export const getDownloadTesterMediasLink = (testId = "", logId = "", v = "", c = "") => {
  return getUrl(`/test/${testId}/task/report/downloadTesterMedias?log=${logId}&v=${v}&c=${c}`);
};

export const PATHS = {
  getUrl,
  goToLink,
  getReportLink,
  getFrontendURL,
  getTestEditLink,
  getTemplateLink,
  getTestShareLink,
  getTestPreviewLink,
  getLoadTemplateLink,
  getBrowserTestShareLink,
  getTemplatesLibraryLink,
  LOGOUT: getUrl("/site/logout"),
  PROFILE: getUrl("/user/profile"),
  NDA: getUrl("/user/profile/nda"),
  DASHBOARD: getUrl("/v2"),
  SURVEYS: getUrl("/v2/surveys"),
  MODERATED_DASHBOARD: getUrl("/v2/moderated"),
  HELP: "https://help.uxarmy.com",
  SUBSCRIPTIONS: getUrl("/credits/subscription/active"),
  UNMODERATED_TEST_SUBSCRIPTION: getUrl("/credits/subscription/"),
  MODERATED_TEST_SUBSCRIPTION: getUrl("/credits/subscription?product=deepdive&billing=monthly"),
  RECRUIT: getUrl("/userpanel/project/"),
  NEW_TEST: getUrl("/v2/test/new"),
  TEAM: getUrl("/user/teamAccount/index"),
  CARDSORTING: getUrl("/v2/toolkit/card-sorting"),
  TREETESTING: getUrl("/v2//toolkit/tree-testing"),
  SUPPORT: "https://help.uxarmy.com/",
  DEEPDIVE_SUPPORT: "https://help.uxarmy.com/space/DeepDive",
  CASE_STUDIES: "https://www.uxarmy.com/case-studies",
  FAQ: "https://help.uxarmy.com",
  TERMS: "https://www.uxarmy.com/terms",
  PRIVACY: "https://www.uxarmy.com/privacy",
  PRICING: "https://www.uxarmy.com/pricing",
  CONTACT_US: "https://www.uxarmy.com/contact-sales",
  CONTACT_SALES: "https://www.uxarmy.com/enterprise-sales",
  SUBSCRIPTION_PLATFORM_CREDITS_HELP: "https://help.uxarmy.com/space/general/196771866/Pooled+Credits",
  SUBSCRIPTION_USERPANEL_CREDITS_HELP: "https://help.uxarmy.com/space/general/196706362/Panel+Credits",
  HOW_CREDITS_WORK: "https://help.uxarmy.com/space/general/196902918/How+UXArmy's+Credit+System+Works",
  SUS_HELP_LINK: "https://help.uxarmy.com/space/UT/89620510/System+Usability+Scale+(SUS)",
  RETRIEVE_PROTOTYPE_LINK: "https://help.uxarmy.com/space/UT/89587772/Retrieving+Prototype's+Public+Link",
  INVITE_TEAM_MEMBERS: "https://help.uxarmy.com/space/general/275906572/Inviting+Team+Members+to+Your+Workspace",
  MODERATED_NEW_PROJECT: "/moderated/create-project",
  getSessionReportLink: (testId) => getUrl(`/test/${testId}/moderated/report`),
  getGoogleIntegrationLink,
  getModeratedEventSlotBookingLink: (eventId, full = false) => {
    const path = `moderated/event/book/${eventId}`;
    return full ? getFrontendURL(path) : `/${path}`;
  },
  getModeratedEventEditLink: (eventId, full = false) => {
    const path = `moderated/event/edit/${eventId}`;
    return full ? getFrontendURL(path) : `/${path}`;
  },
  getModeratedSchedularLink: (testId, full = false) => {
    const path = `moderated/${testId}/scheduler`;
    return full ? getFrontendURL(path) : `/${path}`;
  },
  getModeratedReportLink: (testId, full = false) => {
    const path = `moderated/${testId}/report`;
    return full ? getFrontendURL(path) : `/${path}`;
  },
  getModeratedSlotRescheduleLink: (slotId, full = false) => {
    const path = `moderated/event/booking/${slotId}/reschedule`;
    return full ? getFrontendURL(path) : `/${path}`;
  },
  getModeratedSlotBookingLink: (slotId, full = false) => {
    const path = `moderated/event/booking/${slotId}`;
    return full ? getFrontendURL(path) : `/${path}`;
  },
  getModeratedScheduledSessions: (testId) => {
    return `/moderated/${testId}/report#SCHEDULED`;
  },
  getModeratedAnalysisSpaceLink: (testId) => {
    return `/moderated/${testId}/report#ANALYSIS`;
  },
  getVideoPlayerURL: (params) => {
    const {
      testId = "",
      draftId = "",
      userId = "",
      logId = "",
      taskId = "",
      sessId = "",
      full = true,
      isModerated = false,
      isSharedReport = false,
    } = params || {};

    // logId always exists for unmoderated tests (unlike userId, which is null for client-contact
    // participants) — prefer it, prefixed so VideoPlayerProvider can tell it apart from a legacy userId.
    const idSegment = logId ? `log_${logId}` : userId || sessId;

    let path = `${isModerated ? "moderated" : "test"}/${draftId || testId}/report/videoplayer/${idSegment}/${taskId}`;

    // shared Path
    if (isSharedReport) {
      path = `share/report/${isModerated ? "moderated/" : ""}${draftId || testId}/videoplayer/${idSegment}/${taskId}`;
    }
    return full ? getFrontendURL(path) : `/${path}`;
  },
  getAnalysisSpaceLink: (draftId) => {
    return `/test/${draftId}/${TEST_TAB_KEYS.ANALYSIS_SPACE?.toLowerCase()}`;
  },
  V2: {
    HOME: "/",
    PLANS: "/plans",
    TBT: "/dashboard",
    NORECORDING: "/norecording",
    SURVEYS: "/surveys",
    MODERATED: "/moderated",
    MODERATED_ALL_SCHEDULES: "/moderated/schedules",
    MODERATED_SESSION_SCHEDULER: "/moderated/scheduler",
    TOOLKIT: {
      HOME: "/toolkit",
      CARDSORTING: "/toolkit/card-sorting",
      TREETESTING: "/toolkit/tree-testing",
    },
    EDIT_PROFILE: "/user/edit-profile",
    BILLINGS: "/user/billings",
    CREDITSUSAGE: "/user/credits-usage",
    INTEGRATIONS: "/user/integrations",
    GLOBAL_SETTINGS: "/user/global-settings",
    TOPUP: "/plans/topup",
    ORGANIZATION: "/user/organization",
    PEOPLE: "/user/organization/people",
    WORKSPACE: "/user/organization/workspace",
    ONBOARDING: "/onboarding",
    PARTICIPANTS: {
      LIST: "/participants",
    },
    PROJECTS: {
      LIST: "/projects",
      detail: (projectId) => `/projects/${projectId}`,
    },
    // Every study in the workspace, across every project — the listing the sidebar's All Studies
    // link opens. A project's own Studies tab lives under PROJECTS.detail.
    STUDIES: {
      LIST: "/studies",
    },
    TAGS_LIST: "/tags",
    getTagDetailRoute: (tagId) => `/tags/${tagId}`,
    getUpgradePlanRoute: (planId, recurringType) => `/plans/upgrade/${planId}/${recurringType}`,
    switchWorkspaceRoute: (workspaceId, pathname = "") => `/workspace/${workspaceId}${pathname ? `?return=${pathname}` : ""}`,
  },
  AI_PRIVACY: "https://uxarmy.com/ai-policy/",
  PARTICIPANT_GUIDE_HELP: "https://help.uxarmy.com/space/UT/83460118/System+&+Technical+Requirements",
  UX_AI: "https://uxarmy.com/uxai",
  API: {
    uploadTaskImage: (draftId) => getUrl(`/api/task/uploadTestImages/${draftId}`),
  },
};
